// Narrow-viewport fit detection for the (centered) topbar pill. Toggles
// `.is-compact` when the always-visible inline row (brand + pickers + timestep)
// would overflow the viewport, and reparents the timestep scrubber into the
// actions popup (spanning its top row) so the pill stays compact. Widening
// moves the scrubber back inline.
//
// The decision compares the pill's *full* desired inline width (content + the
// scrubber unit) against the available width. That comparison is invariant to
// the current compact state and to where the scrubber currently lives: when
// inline, `scrollWidth` already includes the unit, so we subtract it; when
// folded into the popup (out of flow), `scrollWidth` excludes it, so we add the
// cached unit back. Either way the verdict is the same desired width, so the
// class toggle / reparent converges in one extra pass and never flaps.

const GAP_PX = 6; // .topbar-menu flex gap; two of them vanish with the unit
const MARGIN_PX = 16; // breathing room at each viewport edge (pill is centered)
const FALLBACK_UNIT_PX = 150; // estimate until the scrubber is measured inline

export function installTopbarFit(): () => void {
    const pill = document.getElementById('topbar-menu');
    const scrubber = document.getElementById('topbar-scrubber');
    const actions = pill?.querySelector<HTMLElement>('.topbar-actions') ?? null;
    if (!pill || !scrubber || !actions) return () => { /* markup missing */ };

    // Width the scrubber + its divider + the two flex gaps reclaim when folded
    // away. Measured only while inline; cached otherwise.
    let cachedUnit = 0;

    // Inline home = directly before the actions popup (after the scrubber
    // divider). Popup home = first grid item (spans the row via CSS).
    const placeScrubber = (compact: boolean): void => {
        if (compact) {
            if (scrubber.parentElement !== actions) actions.insertBefore(scrubber, actions.firstChild);
        } else if (scrubber.parentElement !== pill) {
            pill.insertBefore(scrubber, actions);
        }
    };

    const measure = (): void => {
        const inline = scrubber.parentElement === pill;
        if (inline) {
            const divider = scrubber.previousElementSibling as HTMLElement | null;
            const dividerW = divider ? divider.offsetWidth : 0;
            cachedUnit = scrubber.offsetWidth + dividerW + GAP_PX * 2;
        } else if (cachedUnit === 0) {
            cachedUnit = FALLBACK_UNIT_PX;
        }

        // Desired full inline width, regardless of where the scrubber sits now.
        const desired = (inline ? pill.scrollWidth - cachedUnit : pill.scrollWidth) + cachedUnit;
        const available = window.innerWidth - 2 * MARGIN_PX;
        const compact = desired > available;
        pill.classList.toggle('is-compact', compact);
        placeScrubber(compact);
    };

    let rafId = 0;
    const schedule = (): void => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => { rafId = 0; measure(); });
    };

    window.addEventListener('resize', schedule);
    // Label text (dataset / field) and the scrubber reparent both change the
    // subtree — re-evaluate. Observe content only, not attributes: the
    // `.is-compact` class toggle must not feed back into the observer.
    const mo = new MutationObserver(schedule);
    mo.observe(pill, { childList: true, subtree: true, characterData: true });

    schedule();

    return () => {
        if (rafId) cancelAnimationFrame(rafId);
        window.removeEventListener('resize', schedule);
        mo.disconnect();
        // Restore inline so a re-install (HMR) starts from a clean layout.
        if (scrubber.parentElement !== pill) pill.insertBefore(scrubber, actions);
        pill.classList.remove('is-compact');
    };
}
